<script setup lang="ts">
import { getSiteMapSections } from "~~/shared/site-map";

const localePath = useLocalePath();

const sections = getSiteMapSections();
const totalLinks = sections.reduce((count, section) => count + section.links.length, 0);

usePageSeo({
  title: "Site Map | Ustaad",
  description:
    "HTML site map for all current 11th Computer pages listed in the live study system.",
  keywords:
    "Ustaad site map, 11th computer site map, chapter wise computer pages, computer html sitemap",
  schemaType: "CollectionPage",
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "Site Map", item: "/site-map/" },
  ],
});
</script>

<template>
  <main class="mx-auto max-w-6xl px-4 py-10 text-slate-900 dark:text-slate-100">
    <header class="rounded-[28px] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.14),_transparent_34%),linear-gradient(135deg,_#f8fdff_0%,_#ffffff_48%,_#f8fafc_100%)] p-6 shadow-[0_22px_70px_-44px_rgba(14,165,233,0.55)] dark:border-slate-700 dark:bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.16),_transparent_34%),linear-gradient(135deg,_#020617_0%,_#0f172a_56%,_#111827_100%)] md:p-8">
      <p class="text-xs font-semibold uppercase tracking-[0.24em] text-sky-700 dark:text-sky-300">
        HTML site map
      </p>
      <h1 class="mt-3 text-3xl font-black tracking-tight md:text-5xl">Site Map</h1>
      <p class="mt-3 max-w-3xl text-sm leading-6 text-slate-600 md:text-base dark:text-slate-300">
        HTML site map for the current 11th Computer system. Yeh page un sab URLs ko list karta hai jo sitemap.xml ka hissa hain.
      </p>
      <div class="mt-5 flex flex-wrap gap-3 text-sm text-slate-600 dark:text-slate-300">
        <span class="rounded-full border border-sky-200 bg-white/85 px-3 py-1.5 dark:border-sky-800 dark:bg-slate-900/80">
          {{ sections.length }} sections
        </span>
        <span class="rounded-full border border-sky-200 bg-white/85 px-3 py-1.5 dark:border-sky-800 dark:bg-slate-900/80">
          {{ totalLinks }} main URLs
        </span>
      </div>
    </header>

    <section class="mt-8 grid gap-6 lg:grid-cols-2">
      <article
        v-for="section in sections"
        :key="section.title"
        class="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900"
      >
        <div class="flex items-end justify-between gap-4 border-b border-slate-200 pb-4 dark:border-slate-700">
          <div>
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700 dark:text-sky-300">
              {{ section.links.length }} links
            </p>
            <h2 class="mt-1 text-2xl font-bold">{{ section.title }}</h2>
          </div>
          <NuxtLink
            :to="localePath(section.url)"
            class="text-sm font-semibold text-sky-700 underline decoration-sky-300 underline-offset-4 hover:text-sky-800 dark:text-sky-300 dark:hover:text-sky-200"
          >
            Open section
          </NuxtLink>
        </div>

        <ul class="mt-4 space-y-3">
          <li v-for="link in section.links" :key="link.url" class="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-950/40">
            <NuxtLink
              :to="localePath(link.url)"
              class="text-base font-semibold text-slate-900 hover:text-sky-700 dark:text-white dark:hover:text-sky-300"
            >
              {{ link.title }}
            </NuxtLink>
            <p class="mt-1 break-all text-xs font-medium text-sky-700 dark:text-sky-300">
              {{ link.url }}
            </p>
            <p v-if="link.description" class="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {{ link.description }}
            </p>
          </li>
        </ul>
      </article>
    </section>
  </main>
</template>
